import { ISettings } from '@admin/pages/settings-manager/interfaces/settings.interface';
import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';
import { EntityApiService } from '@core/services/entity-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IBaseEntityData } from '../../core/interfaces/entity-request.interface';

@Injectable({ providedIn: 'root' })
@UntilDestroy()
export class SellerBrandingService {
  private readonly document = inject(DOCUMENT);
  private readonly entityDataService = inject(EntityApiService);

  // Active seller context
  activeSeller = signal<IBaseEntityData | null>(null);
  isBranded = signal(false);
  // Indicates that a branding decision (branded or not) has been resolved
  private readonly decisionMade = signal(false);

  // Exposed CSS variable names
  static readonly VAR_PRIMARY = '--seller-primary-color';
  static readonly VAR_ACCENT = '--seller-accent-color';
  static readonly VAR_BUTTON_BG = '--seller-button-bg';
  static readonly VAR_BUTTON_TEXT = '--seller-button-text';
  // Root class applied when branding active
  static readonly CLASS_BRANDED = 'seller-branded';

  // Defaults (will be overridden by seller settings)
  private readonly defaults = {
    primary: '#146c43', // green primary
    accent: '#c1121f', // red accent
    buttonBg: '#146c43',
    buttonText: '#ffffff',
  };

  private _pendingClear: ReturnType<typeof setTimeout> | null = null;
  constructor() {
    // Initial path-based resolution (no reactive updates needed here)
    effect(
      () => {
        try {
          const path = this.document.location?.pathname || '';
          if (!(path.includes('/seller/') || path.includes('/auction/'))) {
            this.decisionMade.set(true);
          }
        } catch {
          // In SSR with no document.location just mark decision so UI can continue
          this.decisionMade.set(true);
        }
      }
    );
  }

  applyBrandingFromId(idEntity: number) {
    if (idEntity === 1) {
      if (this.isBranded() || this.activeSeller()) {
        this.clearBranding();
      } else {
        this.decisionMade.set(true);
      }
      return;
    }

    // Validate idEntity before attempting network call
    if (
      idEntity == null ||
      Number.isNaN(idEntity) ||
      !Number.isFinite(idEntity) ||
      idEntity <= 0
    ) {
      // Mark decision so UI can proceed with default (non-branded)
      this.isBranded.set(false);
      this.decisionMade.set(true);
      return;
    }
    this.isBranded.set(true);
    this.entityDataService
      .getCachedBaseDataByPk(idEntity)
      .pipe(untilDestroyed(this))
      .subscribe((entity) => {
        if (entity?.isAutonomous && entity.idEntity !== 1) {
          this.applyBranding(entity);
        } else {
          this.clearBranding();
        }
      });
  }

  applyBranding(entity: IBaseEntityData) {
    // Cancel any pending clear
    if (this._pendingClear) {
      clearTimeout(this._pendingClear);
      this._pendingClear = null;
    }
    this.activeSeller.set(entity);
    const root = this.document.documentElement;

    // Placeholder: look for dynamic color fields once backend exposes them (e.g. entity.sellerSetting.primaryColor)
    // Use unknown intermediary to avoid 'any' while backend color fields are unspecified
    const raw: unknown = entity as unknown as { sellerSetting?: unknown };
    const sellerSetting: unknown = (raw as { sellerSetting?: unknown })
      .sellerSetting;
    const ss: Record<string, string> | null =
      sellerSetting && typeof sellerSetting === 'object'
        ? (sellerSetting as Record<string, string>)
        : null;
    const primary = ss?.['primaryColor'] || this.defaults.primary;
    const accent = ss?.['accentColor'] || this.defaults.accent;
    const buttonBg = ss?.['buttonBgColor'] || this.defaults.buttonBg;
    const buttonText = ss?.['buttonTextColor'] || this.defaults.buttonText;

    root.style.setProperty(SellerBrandingService.VAR_PRIMARY, primary);
    root.style.setProperty(SellerBrandingService.VAR_ACCENT, accent);
    root.style.setProperty(SellerBrandingService.VAR_BUTTON_BG, buttonBg);
    root.style.setProperty(SellerBrandingService.VAR_BUTTON_TEXT, buttonText);
    // Add branded class for CSS overrides
    if (!root.classList.contains(SellerBrandingService.CLASS_BRANDED)) {
      root.classList.add(SellerBrandingService.CLASS_BRANDED);
    }
    this.isBranded.set(!!entity.isAutonomous);
    this.decisionMade.set(true);
  }

  clearBranding() {
    const excludePaths = [
      '/home/',
      '/tags/',
      '/search-result-all',
      '/account/',
      '/admin/',
    ];
    const path = this.document.location.pathname;
    const t = excludePaths.find((p) => path.includes(p)) ? 1 : 200;

    // Debounce: wait 100ms; if branding reapplied, cancellation above stops this.
    if (this._pendingClear) {
      clearTimeout(this._pendingClear);
    }
    this._pendingClear = setTimeout(() => {
      const root = this.document.documentElement;
      root.style.removeProperty(SellerBrandingService.VAR_PRIMARY);
      root.style.removeProperty(SellerBrandingService.VAR_ACCENT);
      root.style.removeProperty(SellerBrandingService.VAR_BUTTON_BG);
      root.style.removeProperty(SellerBrandingService.VAR_BUTTON_TEXT);
      root.classList.remove(SellerBrandingService.CLASS_BRANDED);
      this.activeSeller.set(null);
      this.isBranded.set(false);
      this.decisionMade.set(true);
      this._pendingClear = null;
    }, t);
  }

  getBrandHomeUrl(): string {
    return 'home';
  }

  // Expose readiness state
  brandingReady(): boolean {
    return this.decisionMade();
  }

  getAppSettings(): ISettings {
    return {
      urlFacebook: 'https://www.facebook.com/profile.php?id=61580646943490',
      urlTwitter: '',
      urlInstagram: 'https://www.instagram.com/_ridyamo_',
      urlLinkedin: 'https://www.linkedin.com/company/ridyamo',
      urlTiktok: '',
    } as ISettings;
  }
}
